import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, Trash2, ChevronDown } from 'lucide-react';
import { ColDef, ICellRendererParams, SelectionChangedEvent } from 'ag-grid-community';
import { format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { usePagination } from '@/hooks/usePagination';
import { quoteService, type QuoteListRow } from '@/services';
import { profileService } from '@/services';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function Quoting() {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    pageSizeOptions,
  } = usePagination({ defaultPageSize: 25, storageKey: 'quoting' });

  const [quotes, setQuotes] = useState<QuoteListRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<QuoteListRow[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch company ID on mount
  useEffect(() => {
    const fetchCompanyId = async () => {
      if (!user) return;
      const { data: profile } = await profileService.get(user.id);
      if (profile?.company_id) {
        setCompanyId(profile.company_id);
      } else {
        setIsLoading(false);
      }
    };
    fetchCompanyId();
  }, [user]);

  // Fetch paginated quotes
  const fetchQuotes = async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const result = await quoteService.listPaginated(
        companyId,
        { page: pageIndex, pageSize },
        { search: debouncedSearch }
      );
      if (result.error) throw result.error;
      setQuotes(result.data);
      setTotalCount(result.totalCount);
    } catch (error: any) {
      console.error('Error fetching quotes:', error);
      toast({
        title: 'Error',
        description: 'Failed to load quotes.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, [companyId, pageIndex, pageSize, debouncedSearch, toast]);

  const onSelectionChanged = (event: SelectionChangedEvent<QuoteListRow>) => {
    setSelectedRows(event.api.getSelectedRows());
  };

  const handleDeleteConfirm = async () => {
    const quoteIds = selectedRows
      .map((r) => r.quote_id)
      .filter((id): id is string => !!id);

    if (quoteIds.length === 0) return;

    setIsDeleting(true);
    try {
      const { error } = await quoteService.deleteQuotes(quoteIds);
      if (error) throw error;

      toast({
        title: 'Deleted',
        description: `${quoteIds.length} quote${quoteIds.length > 1 ? 's' : ''} deleted successfully.`,
      });

      setSelectedRows([]);
      fetchQuotes();
    } catch (error: any) {
      console.error('Error deleting quotes:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete quotes.',
        variant: 'destructive',
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const formatDate = (value: string | null) => {
    if (!value) return '—';
    try {
      return format(new Date(value), 'MM/dd/yyyy');
    } catch {
      return '—';
    }
  };

  const columnDefs = useMemo<ColDef<QuoteListRow>[]>(() => [
    {
      headerName: 'Vendor',
      flex: 1,
      minWidth: 180,
      sortable: true,
      valueGetter: (params) => params.data?.vendor?.name,
      cellRenderer: (params: ICellRendererParams<QuoteListRow>) => (
        <span className="font-medium">{params.value || '—'}</span>
      ),
    },
    {
      field: 'well_name',
      headerName: 'Well Name',
      width: 150,
      sortable: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'vendor_quote_number',
      headerName: 'Quote #',
      width: 150,
      sortable: true,
      valueFormatter: (params) => params.value || '—',
    },
    {
      field: 'version_number',
      headerName: 'Version',
      width: 100,
      sortable: true,
      type: 'rightAligned',
      valueFormatter: (params) => params.value != null ? String(params.value) : '—',
    },
    {
      field: 'total_price',
      headerName: 'Total Price',
      width: 140,
      sortable: true,
      type: 'rightAligned',
      valueFormatter: (params) => {
        const val = params.value;
        if (val == null) return '—';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(val);
      },
    },
    {
      headerName: 'Status',
      width: 140,
      sortable: true,
      valueGetter: (params) => params.data?.status_name,
      cellRenderer: (params: ICellRendererParams<QuoteListRow>) =>
        params.value ? (
          <span className="badge badge-primary">{params.value}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      field: 'effective_date',
      headerName: 'Effective',
      width: 130,
      sortable: true,
      valueFormatter: (params) => formatDate(params.value),
    },
    {
      field: 'expiration_date',
      headerName: 'Expires',
      width: 130,
      sortable: true,
      valueFormatter: (params) => formatDate(params.value),
    },
  ], []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Supplier Quotes</h1>
          <p className="text-muted-foreground">Manage vendor quotes and pricing submissions.</p>
        </div>
      </div>

      <AGDataGrid<QuoteListRow>
        rowData={quotes}
        columnDefs={columnDefs}
        totalCount={totalCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search well name, API, or quote #..."
        isLoading={isLoading}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        onRowClicked={(row) => row.quote_id && navigate(`/quotes/${row.quote_id}`)}
        rowSelection={{ mode: 'multiRow', checkboxes: true, headerCheckbox: true }}
        onSelectionChanged={onSelectionChanged}
        toolbarActions={
          <>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" disabled={selectedRows.length === 0}>
                  Actions
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button onClick={() => navigate('/quotes/upload')}>
              <Upload className="w-4 h-4 mr-2" />
              Upload Quote
            </Button>
          </>
        }
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quotes</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete {selectedRows.length} quote{selectedRows.length !== 1 ? 's' : ''}? This action is permanent.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
